import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useEffect, useMemo } from "react";
import { createListCollection } from "@ark-ui/react";
import { useField, useForm } from "react-final-form";

import i18n, { useTranslation, useLocale } from "@/lib/i18n";
import { BasicWidgetFormProps } from "@/features/widget-forms/dialogs/widget-dialog-types";
import Select from "@/components/select/select";
import {
  isCoastalMaritimeArea,
  useMaritimeAreasQuery,
} from "@/features/dashboard/components/warnings/maritime-areas.queries";
import { MARITIME_FORECAST_DOMAINS } from "@/features/dashboard/components/warnings/maritime-forecast.domains";
import { useMaritimeForecastQuery } from "@/features/dashboard/components/warnings/maritime-forecast.queries";
import { getAreaName } from "@/features/dashboard/components/warnings/maritime-warnings.utils";
import { filterCoastalAreasForSeichesWidget } from "@/features/shared/widgets/maritime-seiches/maritime-seiches-forecast.utils";
import { normalizeMaritimeSeichesConfiguration } from "@/lib/api";

export function MaritimeSeichesWidgetForm({ values }: BasicWidgetFormProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const areasQuery = useMaritimeAreasQuery();
  const forecastQuery = useMaritimeForecastQuery(MARITIME_FORECAST_DOMAINS.coastal, {
    enabled: areasQuery.isSuccess,
  });

  const initialConfiguration = useMemo(
    () => normalizeMaritimeSeichesConfiguration(values?.configuration),
    [values?.configuration],
  );

  const selectableAreas = useMemo(() => {
    const areas = areasQuery.data ?? [];
    const coastalAreas = areas.filter(isCoastalMaritimeArea);
    return filterCoastalAreasForSeichesWidget(
      coastalAreas,
      forecastQuery.data,
      forecastQuery.isError,
      initialConfiguration.area_id || undefined,
    );
  }, [
    areasQuery.data,
    forecastQuery.data,
    forecastQuery.isError,
    initialConfiguration.area_id,
  ]);

  const areaCollection = useMemo(() => {
    const items = selectableAreas.map((area) => ({
      label: getAreaName(area, locale),
      value: area.id,
    }));
    return createListCollection({ items });
  }, [selectableAreas, locale]);

  const form = useForm();
  const areaIdField = useField<string>("configuration.area_id", {
    initialValue: initialConfiguration.area_id,
    validate: (value) => {
      if (!value) {
        return i18n.t("admin.widgets.fields.maritime_seiches.area_id.validation_required");
      }
    },
  });

  const isLoadingAreas = areasQuery.isLoading;
  const isLoadingForecast =
    areasQuery.isSuccess && !areasQuery.isError && forecastQuery.isLoading;

  useEffect(() => {
    if (isLoadingAreas || isLoadingForecast || areaCollection.items.length !== 1) {
      return;
    }

    const onlyAreaId = areaCollection.items[0].value;
    if (areaIdField.input.value !== onlyAreaId) {
      form.change(areaIdField.input.name, onlyAreaId);
    }
  }, [
    areaCollection.items,
    areaIdField.input.name,
    areaIdField.input.value,
    form,
    isLoadingAreas,
    isLoadingForecast,
  ]);

  if (isLoadingAreas || isLoadingForecast) {
    return (
      <Stack mt={2}>
        <Typography variant="body2">
          {t("admin.widgets.fields.maritime_seiches.area_id.loading", {
            defaultValue: "Loading areas…",
          })}
        </Typography>
      </Stack>
    );
  }

  if (areasQuery.isError || !areaCollection.items.length) {
    return (
      <Stack mt={2}>
        <Typography variant="body2" color="error">
          {t("admin.widgets.fields.maritime_seiches.area_id.load_error", {
            defaultValue: "Could not load maritime areas.",
          })}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack mt={2}>
      <Select
        label={t("admin.widgets.fields.maritime_seiches.area_id.label")}
        collection={areaCollection}
        name={areaIdField.input.name}
        value={areaIdField.input.value}
        onValueChange={(value) => form.change(areaIdField.input.name, value)}
        error={areaIdField.meta.touched && areaIdField.meta.error}
      />
    </Stack>
  );
}
